/**
 * Motion plumbing shared by the #1505 transitions: the reduced-motion
 * check, a scoped same-document View Transition runner, and a reader
 * for how long an element's current CSS animation will run. The rules
 * these serve are in the frontend README's "Motion" section.
 */
import { flushSync } from 'react-dom'

export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

export function canViewTransition(): boolean {
  return (
    typeof document !== 'undefined' &&
    typeof document.startViewTransition === 'function'
  )
}

/** Open transitions per scope class, so an overlapping transition that
 *  skips an earlier one does not strip the class it still needs. */
const openScopes = new Map<string, number>()

export interface ViewTransitionOptions {
  /** Class set on `<html>` while the transition runs. CSS keys the
   *  `view-transition-name`s and the pseudo-element keyframes off it,
   *  so a named element only joins the transition it belongs to. */
  scope?: string
  /** `'skip'` (default) runs the update with no transition under
   *  reduced motion; `'run'` keeps it, for transitions whose CSS
   *  ships a crossfade-only reduced-motion twin. */
  reducedMotion?: 'skip' | 'run'
}

/**
 * Run `update` inside `document.startViewTransition`. React state set
 * synchronously inside `update` is flushed before the browser captures
 * the new state. `update` may return a promise (a route change that
 * commits a frame later); the browser holds the old snapshot until it
 * settles. Without the API, or under reduced motion with `'skip'`,
 * `update` runs straight away.
 */
export function runViewTransition(
  update: () => void | Promise<void>,
  { scope, reducedMotion = 'skip' }: ViewTransitionOptions = {},
): void {
  if (
    !canViewTransition() ||
    (reducedMotion === 'skip' && prefersReducedMotion())
  ) {
    void update()
    return
  }
  const root = document.documentElement
  if (scope) {
    openScopes.set(scope, (openScopes.get(scope) ?? 0) + 1)
    root.classList.add(scope)
  }
  const transition = document.startViewTransition(() => {
    let result: void | Promise<void> = undefined
    flushSync(() => {
      result = update()
    })
    return result
  })
  // A skipped transition rejects `ready`; that is not an error here.
  transition.ready.catch(() => {})
  transition.finished
    .catch(() => {})
    .finally(() => {
      if (!scope) return
      const left = (openScopes.get(scope) ?? 1) - 1
      if (left > 0) {
        openScopes.set(scope, left)
      } else {
        openScopes.delete(scope)
        root.classList.remove(scope)
      }
    })
}

function parseTimes(list: string): number[] {
  return list.split(',').map((raw) => {
    const value = raw.trim()
    const n = parseFloat(value)
    if (Number.isNaN(n)) return 0
    return value.endsWith('ms') ? n : n * 1000
  })
}

/**
 * Milliseconds until `el`'s current CSS animation ends (longest of
 * delay + duration across its animation list), or 0 when it has none.
 * Reads computed style, so the e2e visual stabilizer's zeroed
 * durations and a reduced-motion `animation: none` both come back 0.
 */
export function animationTimeMs(el: Element): number {
  const style = window.getComputedStyle(el)
  if (!style.animationName || style.animationName === 'none') return 0
  const durations = parseTimes(style.animationDuration || '0s')
  const delays = parseTimes(style.animationDelay || '0s')
  let longest = 0
  durations.forEach((d, i) => {
    longest = Math.max(longest, d + (delays[i % delays.length] ?? 0))
  })
  return longest
}
